import { useCallback } from "react";
import { env } from "@/lib/env";

const OAUTH_CALLBACK_PARAMS = [
  "code",
  "state",
  "scope",
  "authuser",
  "prompt",
  "error",
  "error_description",
  "error_uri",
  "iss",
  "session_state",
];

const GOOGLE_RETURN_PATH_KEY = "google_auth_return_path";
// Allowlist email-link flows whose transaction fragment must survive Google login.
// Add new flows here deliberately; accepting arbitrary paths would enable unsafe post-login redirects.
const GOOGLE_RETURN_PATH_PATTERN =
  /^\/(accept-invite|ownership-transfer-accept)#[A-Za-z0-9_-]{22}$/;

export function getGoogleReturnPath() {
  const path = sessionStorage.getItem(GOOGLE_RETURN_PATH_KEY);
  return GOOGLE_RETURN_PATH_PATTERN.test(path) ? path : "/";
}

export function clearGoogleReturnPath() {
  sessionStorage.removeItem(GOOGLE_RETURN_PATH_KEY);
}

// --- PKCE utils ---
function generateCodeVerifier(length = 64) {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "")
    .substring(0, length);
}

async function pkceChallengeFromVerifier(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function generateState(length = 32) {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
}

// --- Main Hook ---
export function useGoogleAuth(returnPath) {
  // Initiates Google login with PKCE flow
  const handleGoogleLogin = useCallback(async () => {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await pkceChallengeFromVerifier(codeVerifier);
    const state = generateState();

    // Store for reading after redirect
    sessionStorage.setItem("google_pkce_code_verifier", codeVerifier);
    sessionStorage.setItem("google_pkce_state", state);
    if (GOOGLE_RETURN_PATH_PATTERN.test(returnPath)) {
      sessionStorage.setItem(GOOGLE_RETURN_PATH_KEY, returnPath);
    } else {
      clearGoogleReturnPath();
    }

    const params = new URLSearchParams({
      client_id: env.google.clientId,
      redirect_uri: env.google.redirectUri,
      response_type: "code",
      scope: env.google.scope,
      access_type: "offline",
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
      state,
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }, [returnPath]);

  // Reads and validates Google callback from URL
  const getGoogleCallbackParams = useCallback(() => {
    const url = new URL(window.location.href);
    const redirectUrl = new URL(env.google.redirectUri, window.location.origin);

    if (
      url.origin !== redirectUrl.origin ||
      url.pathname !== redirectUrl.pathname
    ) {
      return {};
    }

    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");
    const error = url.searchParams.get("error");
    const error_description = url.searchParams.get("error_description");

    OAUTH_CALLBACK_PARAMS.forEach((parameter) =>
      url.searchParams.delete(parameter),
    );
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );

    // Get verifier and state from sessionStorage and clean up
    const codeVerifier = sessionStorage.getItem("google_pkce_code_verifier");
    const originalState = sessionStorage.getItem("google_pkce_state");
    sessionStorage.removeItem("google_pkce_code_verifier");
    sessionStorage.removeItem("google_pkce_state");

    return {
      code,
      state,
      codeVerifier,
      originalState,
      error,
      error_description,
    };
  }, []);

  return {
    handleGoogleLogin,
    getGoogleCallbackParams,
  };
}
